import { useTemplateFromTemplateCreationRecordId } from '@/features/templateBuilder/hooks/useTemplateFromTemplateCreationRecordId';
import { useTemplateSharing } from '@/features/templateBuilder/hooks/useTemplateSharing';
import { TemplateVisibility } from '@heidi/schema/ml-scribe';
import {
  useCombinedQueriesTeamGeneralInfo,
  useOrganizationInfo,
} from '@heidi/workspace/data';
import { useMemo } from 'react';

/**
 * Checks if a user should be blocked from directly editing a team template.
 * Team admins, organisation Template Managers, and Template Managers for their
 * own-team templates can edit; everyone else should clone instead.
 *
 * When true, the template editor should be disabled and clone workflow triggered.
 */
export const useIsNonAdminEditingTeamTemplate = () => {
  const template = useTemplateFromTemplateCreationRecordId();
  const { canManageOwnTeamTemplate } = useTemplateSharing();
  const { isOrganizationTemplateManager } = useOrganizationInfo();
  const { isTeamAdmin } = useCombinedQueriesTeamGeneralInfo();

  const isNonAdminEditingTeamTemplate = useMemo(() => {
    // Not a team template
    if (template?.template_visibility !== TemplateVisibility.Team) {
      return false;
    }

    if (isOrganizationTemplateManager) {
      return false;
    }

    // Template Manager can manage templates for teams they belong to —
    // team-admin-equivalent within their team scope.
    if (canManageOwnTeamTemplate(template)) {
      return false;
    }

    // Team Admin can edit (own team check is done in separate hook)
    if (isTeamAdmin) {
      return false;
    }

    // Non-admin cannot edit any team template
    return true;
  }, [canManageOwnTeamTemplate, isTeamAdmin, isOrganizationTemplateManager, template]);

  return { isNonAdminEditingTeamTemplate };
};
